// La barre d'onglets de Radar au téléphone (lot 2 de la refonte).
//
// SEPT ENTRÉES DEVENAIENT UN BANDEAU QUI DÉFILE de côté au téléphone, où l'on
// ne voyait que les trois premières. Radar en garde trois, qui tiennent sous le
// pouce : Radar, Mes offres, Employeurs. Le reste (CV, réglages, aide et
// extension, langue, thème, déconnexion) passe sous « Moi ». Sur ordinateur,
// la colonne de gauche garde les mêmes trois entrées en tête (cf. `Nav` dans
// App.tsx) : un seul modèle, deux dispositions.

import { useEffect, useState } from 'react'
import { Radar as IconeRadar, Inbox, Building2, UserRound, FileText, Settings, BookOpen, LogOut, X, CreditCard } from 'lucide-react'
import { ThemeToggle, type ThemeChoice } from '@ai-job-fit/ui'
import { navigate, parseHash, retourOffres, type Route } from '../lib/router'
import { useMots } from '../lib/vocabulaire'
import { useT } from '../lib/i18n'
import type { Lang } from '../lib/lang'
import { SelecteurLangue } from './selecteurLangue'

/** L'onglet éclairé pour une route : une offre éclaire l'écran où « Retour »
 *  ramène (l'accueil quand on arrive d'un mail) ; un écran du menu « Moi »
 *  éclaire « Moi ». */
export function ongletRadar(route: Route): 'accueil' | 'offres' | 'employeurs' | 'moi' {
  if (route.name === 'offre') return ongletRadar(parseHash(retourOffres().href))
  if (route.name === 'suivi') return 'offres'
  if (route.name === 'sources' || route.name === 'employeur') return 'employeurs'
  if (route.name === 'profil' || route.name === 'consultant' || route.name === 'config' || route.name === 'aide' || route.name === 'abonnement') return 'moi'
  return 'accueil'
}

export function BarreOnglets({
  route,
  badgeOffres,
  email,
  lang,
  onLang,
  theme,
  onTheme,
  onDeconnexion,
  abonnement = false,
}: {
  route: Route
  badgeOffres: number
  /** L’entrée « Abonnement » apparaît sous « Moi » (tout compte Radar, #112 et #120). */
  abonnement?: boolean
  email: string
  lang: Lang
  onLang: (l: Lang) => void
  theme: ThemeChoice
  onTheme: (c: ThemeChoice) => void
  onDeconnexion: () => void
}) {
  const mots = useMots()
  const t = useT()
  const [menu, setMenu] = useState(false)
  const actif = ongletRadar(route)
  // Changer d'écran referme le menu : sinon il resterait ouvert par-dessus la
  // page qu'on vient de choisir.
  useEffect(() => setMenu(false), [route.name])

  const onglets = [
    { cle: 'accueil', label: mots('nav.decisions'), icone: <IconeRadar size={20} />, aller: '#/' },
    { cle: 'offres', label: mots('nav.suivi'), icone: <Inbox size={20} />, aller: '#/suivi', badge: badgeOffres },
    { cle: 'employeurs', label: mots('nav.sources'), icone: <Building2 size={20} />, aller: '#/sources' },
  ] as const

  return (
    <>
      <nav className="tabbar" aria-label={t('nav.mainAria')}>
        {onglets.map((o) => (
          <button key={o.cle} className={actif === o.cle ? 'on' : ''} aria-current={actif === o.cle ? 'page' : undefined} onClick={() => navigate(o.aller)}>
            <span className="tb-ic">
              {o.icone}
              {'badge' in o && o.badge > 0 && <span className="tb-badge">{o.badge}</span>}
            </span>
            {o.label}
          </button>
        ))}
        <button className={actif === 'moi' || menu ? 'on' : ''} aria-expanded={menu} onClick={() => setMenu((v) => !v)}>
          <span className="tb-ic"><UserRound size={20} /></span>
          {t('radar.moi')}
        </button>
      </nav>

      {menu && (
        <div className="tb-voile" onClick={() => setMenu(false)}>
          <div className="tb-feuille" role="dialog" aria-label={t('radar.menuAria')} onClick={(e) => e.stopPropagation()}>
            <div className="tb-qui">
              <span className="grow">{email}</span>
              <button className="iconbtn" aria-label={t('common.close')} onClick={() => setMenu(false)}><X size={18} /></button>
            </div>
            <button className="tb-item" onClick={() => navigate('#/profil')}><FileText size={18} />{mots('nav.profil')}</button>
            <button className="tb-item" onClick={() => navigate('#/config')}><Settings size={18} />{t('reglages.titre')}</button>
            {abonnement && <button className="tb-item" onClick={() => navigate('#/abonnement')}><CreditCard size={18} />{t('abonnement.titre')}</button>}
            <button className="tb-item" onClick={() => navigate('#/aide')}><BookOpen size={18} />{mots('nav.aide')}</button>
            <div className="tb-reglages">
              <SelecteurLangue value={lang} onChange={onLang} />
              <ThemeToggle value={theme} onChange={onTheme} />
            </div>
            <button className="tb-item" onClick={onDeconnexion}><LogOut size={18} />{t('nav.logout')}</button>
          </div>
        </div>
      )}
    </>
  )
}
